<template>
	<el-dialog :title="$t('customer.batchOpertion')" :visible.sync="dialogVisible" width="60%" :close-on-click-modal="false" @close="handleClose">
		<!-- 步骤条 -->
		<el-steps :active="stepsActiveIndex" align-center finish-status="success" style="margin-bottom: 20px">
			<el-step :title="$t('customer.selectCustomer')"></el-step>
			<el-step :title="$t('customer.importItem')"></el-step>
			<el-step :title="$t('customer.successfully')"></el-step>
		</el-steps>

		<!-- 步骤内容 -->
		<div class="step-content">
			<!-- 第一步 -->
			<div v-show="currentStep === 0">
				<el-table ref="customerTable" :data="customerList" style="width: 100%" max-height="350px" @selection-change="handleSelectionChange">
					<el-table-column type="selection" width="55"> </el-table-column>
					<el-table-column prop="customer_id" :label="$t('customer.customerCode')" width="150">
						<template slot-scope="scope">
							<span>{{ scope.row.customer_id | removeFrontZero }}</span>
						</template>
					</el-table-column>
					<el-table-column prop="customer_name" :label="$t('customer.customerName')"> </el-table-column>
					<el-table-column prop="warehouse" :label="$t('customer.warehouse')" width="140"> </el-table-column>
				</el-table>
			</div>

			<!-- 第二步 -->
			<div v-show="currentStep === 1">
				<div style="display: flex; align-items: center; justify-content: center; gap: 15px; height: 350px">
					<span style="font-weight: 500">{{ $t("customer.batchImportItem") }}</span>
					<el-upload
						action="#"
						:auto-upload="false"
						:file-list="fileList"
						:on-change="handleFileChange"
						:on-remove="handleFileRemove"
						accept=".xlsx,.xls"
						style="display: flex; align-items: center"
					>
						<el-button size="small" type="primary">{{ $t("customer.selectFile") }}</el-button>
						<div v-if="fileList.length === 0" slot="tip" class="el-upload__tip" style="margin-top: 10px">
							{{ $t("customer.noFileSelected") }}
						</div>
					</el-upload>
					<el-button size="small" @click="handleDownloadTemplate">
						{{ $t("customer.templateDownload") }}
					</el-button>
				</div>
			</div>

			<!-- 第三步 -->
			<div v-show="currentStep === 2">
				<div class="complete-message">
					<div v-if="Object.keys(errorItemList).length == 0">
						<el-tag type="success" class="el-tag">{{ $t("customer.importSuccessfully") }}</el-tag>
					</div>
					<div v-if="Object.keys(errorItemList).length > 0">
						<el-tag type="info" class="el-tag" v-for="(item, index) in errorItemList" :key="index">{{ item.level5_code }}{{ item.alias }}</el-tag>
						<p class="failure">
							<span>{{ $t("customer.aTotalOf") }}</span
							>{{ Object.keys(errorItemList).length }}<span>{{ $t("customer.errorTips") }}</span>
						</p>
					</div>
				</div>
			</div>
		</div>

		<!-- 底部按钮 -->
		<span slot="footer" class="dialog-footer">
			<el-button v-if="currentStep > 0 && currentStep <= 2" @click="prevStep">
				{{ $t("customer.previousStep") }}
			</el-button>
			<el-button v-if="currentStep === 0" type="primary" :loading="submitting" @click="handleNextStep" :disabled="isCustomerStepDisabled">
				{{ $t("customer.nextStep") }}
			</el-button>
			<el-button v-if="currentStep === 1" type="primary" :loading="submitting" @click="handleNextStep" :disabled="isFileStepDisabled">
				{{ $t("customer.nextStep") }}
			</el-button>

			<el-button v-if="currentStep === 2" type="primary" @click="handleClose">
				{{ $t("customer.importSuccessfully") }}
			</el-button>
		</span>
	</el-dialog>
</template>

<script>
import { postbatchUpdateAliases, downloadTemplate } from "@/services/api-v2/api-v2";

export default {
	name: "CustomerBatchOperationDialog",
	props: {
		visible: {
			type: Boolean,
			default: false,
		},
		customerList: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			dialogVisible: this.visible,
			currentStep: 0,
			submitting: false,
			fileList: [],
			selectedCustomers: [],

			errorItemList: [],
		};
	},
	watch: {
		visible(val) {
			this.dialogVisible = val;
			if (val) {
				// 重置状态
				this.resetDialogState();
			}
		},
		dialogVisible(val) {
			this.$emit("update:visible", val);
		},
	},
	methods: {
		// 处理表格选择变化
		handleSelectionChange(selection) {
			this.selectedCustomers = selection;
		},
		// 文件改变时
		handleFileChange(file, fileList) {
			if (fileList.length > 1) {
				this.fileList = [fileList[fileList.length - 1]];
			} else {
				this.fileList = fileList;
			}
		},
		// 文件删除
		handleFileRemove(file, fileList) {
			this.fileList = fileList;
		},

		// 下载模板
		handleDownloadTemplate() {
			downloadTemplate()
				.then((response) => {
					const blob = new Blob([response.data], {
						type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
					});

					// 下载
					const url = window.URL.createObjectURL(blob);
					const link = document.createElement("a");
					link.href = url;
					link.download = "customer_template.xlsx";
					document.body.appendChild(link);
					link.click();
				})
				.catch((error) => {
					this.$message.error(error);
				});
		},
		// 下一步
		async handleNextStep() {
			if (this.currentStep === 0) {
				this.currentStep++;
				return;
			}

			if (this.currentStep === 1) {
				// 调用上传接口
				await this.handleSubmit();
				return;
			}
		},
		// 上一步
		prevStep() {
			this.currentStep--;
		},
		// 提交
		async handleSubmit() {
			try {
				this.submitting = true;
				const formData = new FormData();
				const customerIds = this.selectedCustomers.map((c) => c.customer_id);

				customerIds.forEach((id) => {
					formData.append("customer_ids[]", id);
				});

				formData.append("file", this.fileList[0].raw);

				const { data } = await postbatchUpdateAliases(formData);

				if (data.length > 0) {
					this.errorItemList = data;
				}
				// 上传成功进入第三步
				this.currentStep = 2;
			} catch (error) {
				this.$message.error(error);
			} finally {
				this.submitting = false;
			}
		},
		// 关闭弹窗
		handleClose() {
			this.dialogVisible = false;
			this.$emit("close");
		},
		// 重置状态的逻辑优化
		resetDialogState() {
			// 立即重置数据状态
			this.currentStep = 0;
			this.fileList = [];
			this.selectedCustomers = [];
			this.errorItemList = [];
			this.submitting = false;

			//清空表格选择 ， 更新DOM
			this.$nextTick(() => {
				if (this.$refs.customerTable) {
					this.$refs.customerTable.clearSelection();
				}
			});
		},
	},
	computed: {
		isCustomerStepDisabled() {
			return this.selectedCustomers.length === 0;
		},
		isFileStepDisabled() {
			return this.fileList.length === 0;
		},
		// 当在第三步时，将步骤条的 active 设置为 3，使所有步骤都显示为完成
		stepsActiveIndex() {
			return this.currentStep === 2 ? 3 : this.currentStep;
		},
	},
};
</script>

<style scoped>
.step-content {
	margin: 30px 0;
	min-height: 300px;
	position: relative;
}

.step-content > div {
	transition: opacity 0.2s ease-in-out;
}

.step-content > div[style*="display: none"] {
	position: absolute;
	visibility: hidden;
	pointer-events: none;
}

.step-content h3 {
	margin-bottom: 20px;
	color: #333;
	font-size: 18px;
}

.confirm-content {
	padding: 20px;
	background-color: #f5f7fa;
	border-radius: 4px;
	margin-top: 20px;
}

.confirm-content p {
	margin: 8px 0;
	color: #606266;
	font-size: 14px;
}

.confirm-content strong {
	color: #409eff;
	font-weight: 600;
}

.upload-demo {
	display: block;
}

.el-upload__tip {
	color: #909399;
	font-size: 12px;
}
.complete-message {
	height: 350px;
	overflow: auto;
	box-sizing: border-box;
}
.failure {
	color: #f56c6c;
	padding-left: 10px;
}
.el-tag {
	margin: 0 10px 10px 0;
}
</style>
